// mentorship-de.js — Integrales Mentorship DE (archive 2024-11-14, second-pass full narrative)
// Source: https://web.archive.org/web/20241114145146/https://academy.benjaminkurtz.de/mentorship
// Components use useContent() — never hardcode strings.

// Served from Cloudflare R2 (bk-aca) via media.thelightcall.com — see mentorship-r2-upload manifest
const ASSET = 'https://media.thelightcall.com/mentorship'

export const meta = {
  title: 'Integrales Mentorship — Benjamin Kurtz | The Light Call',
  description:
    'Integrales Mentorship mit Benjamin Kurtz. Committe dich für dein Leben in WOW. Erstgespräch kostenlos und unverbindlich.',
}

export const header = {
  nav: [
    { label: 'Mentorship', anchor: '#was-ist' },
    { label: 'Freiheit', anchor: '#freiheit' },
    { label: 'Ergebnisse', anchor: '#cases' },
    { label: 'Über Benjamin', anchor: '#welcome' },
    { label: 'Gespräch', anchor: '#booking' },
  ],
  cta: 'Erstgespräch',
}

// Kept for shared chrome that may still read hero keys
export const hero = {
  label: 'INTEGRALES MENTORSHIP',
  headlineLine1: 'Du willst großartige',
  headlineLine2: 'Ergebnisse?',
  heroCta: 'Committe dich für dein Leben in WOW!',
  subline: 'Alles okay so wie es ist? Kein Commitment notwendig.',
  ctaPrimary: 'Erstgespräch vereinbaren',
  ctaSecondary: 'Mehr erfahren →',
  videoUrl: '',
  posterUrl: `${ASSET}/hero-fullbleed.jpg`,
  vimeoEmbedUrl: '',
}

/** Universal: HeroImageSection — two full dual-path sets + single CTA */
export const heroImage = {
  id: 'hero',
  backgroundUrl: `${ASSET}/hero-fullbleed.jpg`,
  // Real rgba darken via resolveImageOverlay (bg-color-primary/NN is a no-op on custom utils)
  overlayOpacity: 0.58,
  overlayColor: 'navy',
  dualPaths: [
    {
      key: 'wow',
      variant: 'wow',
      headline: 'Du willst großartige Ergebnisse?',
      line: 'Committe dich für dein Leben in WOW!',
    },
    {
      key: 'okay',
      variant: 'okay',
      headline: 'Alles ganz okay so wie es ist?',
      line: 'Kein Commitment notwendig.',
    },
  ],
  // Legacy keys (still used by reduced-motion fallback builders / EN mirrors)
  headline: 'Du willst großartige Ergebnisse?',
  pathWow: 'Committe dich für dein Leben in WOW!',
  pathOkay: {
    headline: 'Alles ganz okay so wie es ist?',
    line: 'Kein Commitment notwendig.',
  },
  cta: 'Vereinbare jetzt dein Erstgespräch',
}
export const fullBleedHero = heroImage

/** Universal: FullBleedCTASection instances */
export const counterbalance = {
  id: 'gegenpol',
  surface: 'image',
  variant: 'onDark',
  backgroundUrl: `${ASSET}/bg-counterbalance-office.jpg`,
  // Brand navy darken (never black) — keep α mid so office photo still reads
  overlayOpacity: 0.6,
  overlayColor: 'navy',
  headline: 'Andere fragen dich, welches Umsatzziel du erreichen willst…',
  titleParts: [
    // Archive: red word-mark on photo band
    { text: 'Andere', accent: 'emphasis' },
    { text: 'fragen dich, welches Umsatzziel du erreichen willst…', accent: false },
  ],
}

export const brickBand = {
  id: 'brick',
  surface: 'image',
  variant: 'onDark',
  backgroundUrl: `${ASSET}/kungfu-brick.jpg`,
  overlayOpacity: 0.5,
  overlayColor: 'navy',
  headline:
    'Kung Fu Meister fühlen ihre Hand unter dem zertrümmerten Backstein, BEVOR sie auch nur einen Muskel bewegen.',
  // Intro paras only (paper panel) — mentee bullets moved to 2×2 cards
  bodyBox: true,
  bodyBoxVariant: 'paper',
  body: [
    'Und so würde auch die Freude über den bereits gefallenen 1m hohen Dominostein deine Energie in die richtige Richtung lenken.',
    'Übertragen auf dein Leben bedeutet das: Du bringst Energie in Bewegung und veränderst so unmittelbar deine Welt. Jeden Tag. Jede Stunde. Jede Woche.',
    'Und mit den richtigen Zielbildern stellst du sicher, dass dir das Ergebnis auch gefällt. ;)',
  ],
  // Section headline + 2 columns × 2 rows (TopicCard craft)
  bodyCardsHeadline: 'Deshalb unterstütze ich meine Mentees im integralen Mentorship dabei:',
  bodyCardsColumns: 2,
  bodyCards: [
    {
      body: '… neu zu denken, um neu fühlen & neu handeln zu können.',
    },
    {
      body: '… sich darauf einzulassen, dass Langsamkeit manchmal der schnellste Weg nach vorne ist &',
    },
    {
      body: '… auch in herausfordernden Situationen zu erkennen, dass sie in jedem Moment frei sind, neu zu wählen.',
    },
    {
      body: '… herauszufinden, was der 5 mm Dominostein in ihrem Leben ist, der alles verändern kann.',
    },
  ],
}

/** Full-bleed intro before outcomes list (white type on dark image) */
export const outcomesIntro = {
  id: 'ergebnisse-intro',
  surface: 'image',
  variant: 'onDark',
  backgroundUrl: `${ASSET}/benjamin-portrait-25.jpg`,
  overlayOpacity: 0.55,
  overlayColor: 'navy',
  headline: 'So kommst DU ganz leicht zu deinen Ergebnissen:',
  // motion library bounce chevron → outcomes list
  scrollCue: true,
  scrollTarget: '#ergebnisse-liste',
}

// legacy alias used by older code paths
export const kungFuBand = brickBand

export const services = {
  items: [
    { label: 'Klarheit im Kern', sub: 'Wer du wirklich bist' },
    { label: 'Intensive 1:1 Begleitung', sub: 'Dranbleiben statt einmalig' },
    { label: 'Leben in Fülle', sub: 'Ergebnisse im Einklang mit Herz & Seele' },
  ],
  location: 'online & weltweit',
}

export const whatIs = {
  label: 'WO LIEGT EIGENTLICH DAS PROBLEM?',
  headline: 'Das wäre eigentlich der Teil, wo ich über dein Problem spreche 😅',
  body: [
    'Neben der Tatsache, dass ich davon überzeugt bin, dass ein außergewöhnliches Leben den Fokus auf die Lösung und nicht das Problem voraussetzt, weißt du am allerbesten, wonach du dich gerade am meisten sehnst.',
    'Dir ist bereits bewusst, dass dieses (eine?) Leben unglaublich viele Möglichkeiten für dich bereit hält. Dein Leben ist bereits bunt. Und du hast erlebt, dass du Großartiges erreichen kannst.',
    'Die entscheidende Frage, die du dir stellen darfst: „Ist es gut so, wie es ist?“ Ja? Herzlichen Glückwunsch. Nein? Dann liegt es in deiner Verantwortung, etwas in deinem Leben zu verändern.',
    'Vielleicht kannst du selbst noch nicht ausdrücken, was genau dir fehlt. Alles, was du weißt, ist, dass es an der Zeit ist, deinen eigenen Weg zu entdecken. Du hast lange erfolgreich alles gemacht wie alle anderen, aber dir gehen die Vorbilder aus.',
    'Du wünschst dir irgendwie „anders“. Nicht unbedingt mehr … (mehr Umsatz, mehr Geld, mehr Freiheit, mehr Abwechslung) — sondern anders. Ein Leben, das ein überschwänglicher Ausdruck der Person ist, die du im Kern bist.',
    'Du hättest gerne eine Antwort auf die Frage, was dein „anders“ ist? Dann lass uns in Kontakt kommen und den ersten Schritt gemeinsam gehen. Mein Versprechen an dich: neue Perspektiven, neue Herangehensweisen und vor allem konkrete Lösungsansätze. Probier es unverbindlich aus.',
  ],
}

/**
 * FullCTA — full-bleed puzzle band (archive Freedom / Umsatz-Puzzle).
 * Image: bg-puzzle.jpg · red emphasis on main claim.
 */
export const puzzleBand = {
  id: 'freiheit',
  surface: 'image',
  variant: 'onDark',
  backgroundUrl: `${ASSET}/bg-puzzle.jpg`,
  overlayOpacity: 0.5,
  overlayColor: 'black',
  heightPercent: 100,
  titleParts: [
    { text: '…ich bin davon überzeugt:', accent: false, block: true },
    {
      text: 'Umsatz ist nur ein Teil des Puzzles.',
      accent: 'emphasis',
      block: true,
    },
  ],
  headline: '…ich bin davon überzeugt: Umsatz ist nur ein Teil des Puzzles.',
  body: [
    'Denn hinter dem Wunsch nach finanziellem Erfolg versteckt sich die Sehnsucht nach etwas ganz anderem: der Freiheit tun und lassen zu können, was man - nein, DU - willst.',
    'Auf den ersten Blick ist die Verbindung zwischen Umsatz und Freiheit nachvollziehbar.',
    'Schließlich macht Geld es viel einfacher, dir dein Leben nach deinen Vorstellungen zu gestalten - wann und wo immer du willst. Meine Vorstellung von Freiheit allerdings sieht anders aus …',
  ],
}

/** @deprecated alias — was Body mediaText */
export const mediaText = puzzleBand
export const freedomPuzzle = puzzleBand

/**
 * Statement — navy/blue band under puzzle (archive green freedom thesis).
 */
export const freedomStatement = {
  id: 'wahre-freiheit',
  variant: 'blue',
  lines: [
    // White on navy (no green accent)
    {
      text: 'Wahre Freiheit bedeutet, sich jederzeit entscheiden zu können, was in diesem Moment genau das Richtige für dich ist.',
    },
    {
      text: 'Auch wider der Erwartungen anderer.',
    },
  ],
}

/** BodySection beat A (story) — archive red/green on Nein? */
export const storyPartA = {
  id: 'story-a',
  label: '1 % · EXPONENTIELLES DENKEN',
  headline: 'Wie wärs mit einer neuen Art, über dein Leben zu denken?',
  imageUrl: `${ASSET}/domino-line.jpg`,
  imageAlt: 'Erster Schritt und außergewöhnliches Leben',
  question:
    'Glaubst du, dass ein 5 Millimeter großer Dominostein einen 1 Meter hohen Dominostein umwerfen kann?',
  answerLead: 'Nein? Nein.',
  // Archive: Nein? red · Nein. green
  answerLeadParts: [
    { text: 'Nein?', accent: 'emphasis' },
    { text: ' ', accent: false },
    { text: 'Nein.', accent: 'highlight' },
  ],
  body: [
    'Zumindest nicht, wenn man „normaler“ Logik folgt. Denn die basiert auf linearem Denken, dem klassischen Ursache-Wirkungs-Prinzip.',
    'Was aber, wenn du die Situation ganzheitlicher betrachtest?',
    'Ein Dominostein kann einen anderen Dominostein umwerfen, der 1,5 Mal so groß ist, wie er selbst.',
    'Mit nur 12 Dominosteinen wird es dadurch möglich, dass ein winziger Stein einen 1m Klotz zu Fall bringt.',
    'Alles, was du brauchst, ist die Energie des ersten, 5mm großen, fallenden Steins.',
    {
      parts: [
        { text: 'Das ist ', accent: false },
        { text: 'exponentielles Denken', accent: 'highlight' },
        { text: '.', accent: false },
      ],
    },
  ],
}

/** StatementSection between story beats */
export const storyQuote = {
  id: 'story-quote',
  variant: 'light',
  lines: [
    {
      text: 'Andere mögen das Geheimnisse nennen. Ich zeig dir, wie du es ganz einfach umsetzt.',
    },
    {
      text: 'Kein Chichi, kein Klimbim. Ausprobieren und ERLEBEN.',
      accent: 'highlight',
    },
  ],
}

/** BodySection beat B (story) — eyebrow → H2 → image → body → closing */
export const storyPartB = {
  id: 'story-b',
  label: '1 % · EXPONENTIELLES DENKEN',
  headlineParts: [
    {
      text: 'Lass uns aber noch einen Schritt weiter gehen.',
      accent: 'highlight',
    },
  ],
  // imageUrl (not imageChain) so BodySection paints image before body
  imageUrl: `${ASSET}/domino-blocks.jpg`,
  imageAlt: 'Dominokette — vom ersten Schritt zum Ziel',
  body: [
    'Denn ehrlich: es gibt auch in diesem Fall eine Ursache (5mm Stein) und eine Wirkung (Fall des 1m Klotzes).',
    'Glaubst du daran, dass du Dinge — gute wie schlechte — selbst in dein Leben ziehen kannst?',
    'Hast du die Kraft der Manifestation vielleicht schon selbst erlebt?',
    'Dann weißt du, dass es nicht immer konkrete Handlungen sind, die dich deinem 1m Klotz (deinem Zielbild) näher bringen.',
  ],
  // Archive green thesis
  closingParts: [
    {
      text: 'Deine Gedanken und … noch viel wichtiger, deine Gefühle manifestieren unmittelbar.',
      accent: 'highlight',
    },
  ],
  manifestExamples:
    'Egal, ob das der Stress mit deinem Chef oder die Begegnung mit deinem:r Traumpartner:in ist.',
}

export const storySequence = storyPartA
export const dominoStory = storyPartA
export const quoteBand = storyQuote
export const secretsQuote = storyQuote

/** ProcessSection content (from former pillars) */
export const pillars = {
  id: 'prozess',
  label: 'LEBEN IN FÜLLE',
  headline: 'So katapultiert dich das Integrale Mentorship in dein Leben in Fülle:',
  footnote: 'Drei Säulen der Zusammenarbeit — Klarheit, Begleitung, Grundlage für ein erfülltes Leben.',
  steps: [
    {
      title: 'Gewinne Klarheit darüber, wer du im Kern bist',
      titleParts: [
        { text: 'Gewinne ', accent: false },
        { text: 'Klarheit', accent: 'primary' },
        { text: ' darüber, wer du im Kern bist', accent: false },
      ],
      duration: 'Selbsterkenntnis',
      body: '(& welche Konsequenzen sich daraus für dein Leben, deine Beziehungen und dein Business ergeben). Selbsterkenntnis ist die Superpower der Zukunft. Indem du das Unbewusste bewusst machst, triffst du Entscheidungen trotz verlockender Alternativen selbstsicher.',
    },
    {
      title: 'Intensive Begleitung statt punktuelle Unterstützung',
      titleParts: [
        { text: 'Intensive', accent: 'primary' },
        { text: ' Begleitung statt punktuelle Unterstützung', accent: false },
      ],
      duration: '1:1 Commitment',
      body: 'Regelmäßige 1:1 Sessions haben den Vorteil, dass du gar nicht anders kannst als dranzubleiben. Damit ist das Mentoring ein klares Commitment dafür, dein Leben aktiv zu gestalten — und Wünsche wahr werden zu lassen (auch die, von denen du heute noch nicht zu träumen wagst).',
    },
    {
      title: 'Lege die Grundlage für ein zutiefst erfülltes Leben',
      titleParts: [
        { text: 'Lege die Grundlage für ', accent: false },
        { text: 'ein zutiefst erfülltes Leben', accent: 'primary' },
      ],
      duration: 'Ergebnis',
      body: 'Du lernst, Ziele in Freude und Gelassenheit zu erreichen und Herausforderungen schnell zu lösen. Das Ziel: Schritt für Schritt Ergebnisse, die dich zutiefst erfüllen — im Einklang mit Herz, Seele & Verstand.',
    },
  ],
  midCta: {
    headline: 'Lass uns in Kontakt kommen!',
    body: '30 Minuten Du & Ich. Mein Versprechen: neue Perspektiven, neue Herangehensweisen und vor allem konkrete Lösungsansätze. Du kannst also nur gewinnen.',
    cta: 'Jetzt direkt Ergebnisse bekommen',
  },
  cta: 'Jetzt direkt Ergebnisse bekommen',
}

export const mentorshipBenefits = {
  label: 'LEBEN IN FÜLLE',
  headline: 'So katapultiert dich das Integrale Mentorship in dein Leben in Fülle:',
  items: [
    {
      iconUrl: `${ASSET}/benefit-1.png`,
      titleParts: [
        { text: 'Gewinne', accent: false },
        { text: 'Klarheit', accent: true },
        { text: 'darüber, wer du im Kern bist', accent: false },
      ],
      subline:
        '(& welche Konsequenzen sich daraus für dein Leben, deine Beziehungen und dein Business ergeben)',
      body: 'Selbsterkenntnis ist die Superpower der Zukunft. Indem du das Unbewusste bewusst machst, bekommst du Klarheit darüber, was als Nächstes ansteht und kannst Entscheidungen trotz verlockender Alternativen selbstsicher treffen.',
    },
    {
      iconUrl: `${ASSET}/benefit-2.png`,
      titleParts: [
        { text: 'Intensive', accent: true },
        { text: 'Begleitung statt punktuelle Unterstützung', accent: false },
      ],
      subline: '',
      body: 'Regelmäßige 1:1 Sessions haben den Vorteil, dass du gar nicht anders kannst als dranzubleiben. Damit ist das Mentoring ein klares Commitment dafür, dein Leben aktiv zu gestalten. Und deine Wünsche wahr werden zu lassen (auch die, von denen du heute noch nicht zu träumen wagst)!',
    },
    {
      iconUrl: `${ASSET}/benefit-3.png`,
      titleParts: [
        { text: 'Lege die Grundlage für', accent: false },
        { text: 'ein zutiefst erfülltes Leben', accent: true },
      ],
      subline: '',
      body: 'Du lernst im Mentoring nicht nur, wie du deine Ziele in Freude und Gelassenheit erreichst, sondern auch Herausforderungen schnell und effizient zu lösen. Es geht nicht nur darum, groß zu träumen. Das Ziel des Mentorships ist es, Schritt für Schritt Ergebnisse zu manifestieren, die dich zutiefst erfüllen. Im Einklang mit Herz, Seele & Verstand. Wenn du willst, geht das sehr leicht.',
    },
  ],
  midCta: {
    headline: 'Lass uns in Kontakt kommen!',
    body: '30 Minuten Du & Ich. Mein Versprechen: neue Perspektiven, neue Herangehensweisen und vor allem konkrete Lösungsansätze. Du kannst also nur gewinnen.',
    cta: 'Jetzt direkt Ergebnisse bekommen',
  },
}

/**
 * QuoteSection — Fitzgerald band on archive full-bleed duality image
 * (Kajabi section 1606248060697 / Duality_Plus_Kategorien_und_Ads.jpg)
 */
export const quoteSection = {
  id: 'quote',
  surface: 'image',
  backgroundUrl: `${ASSET}/bg-quote-duality.jpg`,
  overlayOpacity: 0.58,
  overlayColor: 'navy',
  quotes: [
    {
      lines: [
        "I hope you live a life you're proud of,",
        "and if you find that you're not,",
        'I hope you have the strength to start all over again.',
      ],
      author: 'F. S. Fitzgerald',
    },
  ],
}

export const welcome = {
  headline: 'Hi, ich bin Benjamin.\nSchön, dass du hier bist.',
  quoteLines: [
    'Mein oberstes Lebensziel heute: meinem ureigenen Weg zu folgen.',
    'Ich durfte lernen, wie ich ihm auch dann folge, wenn das Leben um mich herum mal wieder laut tost — und wie ich damit umgehe, nicht von jedem für meine Entscheidungen gemocht zu werden.',
    'Meine Erkenntnis: Der Mut, es trotz aller vermeintlichen Hindernisse zu tun, zahlt sich aus. Das macht dich frei.',
    'Denn das Gefühl, wenn du merkst, am genau richtigen Platz mit genau den richtigen Menschen zu sein und genau das zu tun, was dich erfüllt, ist unbeschreiblich.',
  ],
  author: 'Benjamin',
  imageUrl: `${ASSET}/benjamin.jpg`,
  signatureUrl: 'https://media.thelightcall.com/images/signature_benjamin_black.png',
}

// Archive end recommit (before FAQ) — dual path like hero
export const statement = {
  variant: 'light',
  lines: [
    { text: 'Du willst großartige Ergebnisse?' },
    // Archive green highlight lines
    { text: 'Committe dich für ein großartiges Leben.', accent: 'highlight' },
    { text: 'Alles okay so wie es ist?' },
    { text: 'Kein Commitment notwendig.', accent: 'highlight' },
  ],
}

export const about = {
  label: 'ÜBER BENJAMIN',
  headline: 'Was mich zu dieser Arbeit gerufen hat',
  body: [
    'Auch ich bin hineingewachsen in ein Leben geleitet von einem höheren Ganzen, ausgedrückt durch meine Intuition. Diese scheinbar leise Stimme, die immer schon da war, führte dazu, dass ich ganz automatisch von bestimmten Dingen angezogen wurde.',
    'Irgendwann stellte ich fest, dass sich im Laufe der Jahre immer mehr Zertifikate angesammelt hatten. Ohne es zu realisieren, war ich inzwischen NR-Master-Practitioner & wingwave Coach®, ausgebildeter TrainTheTrainer und praktizierte außerdem seit über 20 Jahren Meditation und Energiearbeit wie Matrix-Power oder Reiki.',
    'Denn das alles fand parallel zu meinem Job als Fotograf & Filmemacher in der Welt der Fortune500 statt, der mir lange Zeit ein wunderbarer Lebensabschnittsgefährte war und dessen Skills mir heute noch wertvolle Dienste leisten.',
    'Der Wandel zu meiner jetzigen Arbeit als Mentor, Trainer und Coach begann, als ich mein Talent, Menschen in nur wenigen Momenten spüren und einschätzen zu können, mehr & mehr nutzte, um fantastische Ergebnisse in der Portraitfotografie zu erzielen. Daraus entwickelte sich mein erstes Coachingprodukt: die integrale Portraitfotografie.',
    'Als hätte ich eine Quelle angezapft, begann meine Kreativität immer weiter zu sprudeln. Ich bot erste Trainings und Seminare an und merkte, dass diese Form der Arbeit mir nicht nur zu 100 % entspricht, sondern ich damit gleichzeitig Menschen tief berühre.',
    'Heute weiß ich, wie laut diese Stimme der Intuition eigentlich schon immer war. Sie war nur auf der anderen Seite der Scheibe. Jetzt gibt es keine Scheibe mehr zwischen Bewusstsein und Intuition.',
    'Meine Arbeit richtet sich an diejenigen, die an einem Punkt in ihrem Leben stehen, an dem sie tiefer gehen wollen. Menschen, die ihr Leben bereits aktiv gestalten, aber immer wieder an unsichtbare Grenzen stoßen. Heißt: Ich mache da weiter, wo andere nicht weiterkommen.',
  ],
  credentials: [
    'Integrales Mentorship',
    'NLP / NR Master',
    'wingwave Coach®',
    'Train the Trainer',
    'Energiearbeit & Reiki',
  ],
  credentialsLabel: 'ANSATZ & METHODEN',
  photoPlaceholder: '[ Foto Benjamin Kurtz ]',
}

export const forWhom = {
  label: 'FÜR WEN',
  headline: 'Für wen das Mentoring Programm geschaffen wurde',
  intro:
    'Die schlechte Nachricht: Nur du kannst die Frage „wie sieht mein anders aus“ beantworten. Denn niemandem ist dein Business, dein inneres Wohlbefinden, … dein Leben so wichtig wie dir. Die gute Nachricht: Wenn du dir die Zeit nimmst, konsequent hinzuschauen und ein Bewusstsein darüber zu entwickeln, wer du im Kern bist … wirst du unaufhaltsam.',
  columns: 3,
  topics: [
    {
      title: 'Träume umsetzen — ohne Ablenkung',
      body: 'Du setzt deine Träume bereits aktiv um, lässt dich aber immer wieder von „spannenden“ Projekten ablenken.',
    },
    {
      title: 'Hungrig aufs Leben',
      body: 'Du bist hungrig aufs Leben und kannst es kaum erwarten zu erleben, was passiert, wenn du die Handbremse löst (aka. dein volles Potenzial lebst).',
    },
    {
      title: 'Gegen den Strom',
      body: 'Du hast keine Lust mehr, blind gesellschaftlichen Standards zu folgen & bist bereit, auch mal gegen den Strom zu schwimmen (wenn sich das richtig für dich anfühlt).',
    },
    {
      title: 'Herz, Verstand & Seele',
      body: 'Du triffst Entscheidungen aus dem Herz und dem Verstand (& bist interessiert daran, auch noch eine 3. Komponente mit einzubeziehen: deine Seele).',
    },
    {
      title: 'Bereit, tiefer zu gehen',
      body: 'Du hast bereits viel in deinem Leben erreicht und willst jetzt tiefer gehen — auch wenn das bedeutet, dass du unbekanntes Terrain betrittst.',
    },
  ],
}

/** ProcessSection binds this key (pillars content) */
export const processSection = pillars

export const cases = {
  label: 'ERGEBNISSE',
  headline: 'Das sind die Ergebnisse meiner Kund:innen',
  body: 'Am Ende ist es egal, wer du bist und wo du herkommst. Meine Kund:innen verbindet eine gemeinsame Mission: Ein außergewöhnliches Leben führen.',
  anonymityNote: '',
  clickHint: 'Klicke auf eine Geschichte, um sie zu lesen.',
  items: [
    {
      name: 'Tobias Hummel',
      gender: 'male',
      image: `${ASSET}/tobias.jpg`,
      title: 'Emotional Release Coach',
      teaser:
        'Starke Schmerzen, Umsetzungs-Schwäche, Zweifel an Berufung und Beziehung — und dann der Durchbruch.',
      tag: 'Mentorship · Berufung · Beziehung',
      situation:
        'Tobias startete seine Reise mit mir in 4 Coaching Sessions (The Insightful Four Package). Er hatte starke Schmerzen körperlich in seinen Organen. Er fühlte sich umsetzungsschwach und war unzufrieden in seinem Job. Er verspürte immer wieder starke Zweifel über seine Berufung sowie in seiner Beziehung.',
      session:
        'Noch mitten im Mentorship ist Tobias grandios über sich selbst hinausgewachsen. Keine Zweifel mehr, dass er als Coach arbeitet, auch in seinem Job steht eine tolle Veränderung an. Seine Beziehung ist wie in den Flitterwochen. Er hat zu einem gesunden Körpergefühl und vor allem zu seiner Liebe und Intuition zurückgefunden.',
      result:
        '„Ich bin Benjamin unendlich dankbar für diesen unglaublichen Durchbruch. Er hat mir gezeigt, wie mächtig meine eigenen Gedanken und Gefühle sind, um alte Schmerzen und Verspannungen loszulassen. Durch seine Unterstützung habe ich gelernt, meine größten Ängste zu überwinden, mich selbst zu befähigen, so dass ich die Kraft habe, ein großartiger Coach zu werden.“',
    },
    {
      name: 'Anna Gräfen',
      gender: 'female',
      image: `${ASSET}/anna.jpg`,
      title: 'Health Coach',
      teaser:
        'Nach dem Verlust ihres Partners: Business neu gebaut, Umsatz vervierfacht, Lebensfreude zurück.',
      tag: 'Mentorship · Business · Heilung',
      situation:
        'Anna hat 3 Jahre vor dem Start meines Mentorships „Leben in WOW!“ ihren Partner bei einem tragischen Unfall verloren. Ihre Verarbeitung war erstaunlich, aber sie hat viel weggesteckt und ist hart geworden, durch Bodybuilding und Flucht in die Arbeit. Sie fühlte sich oft alleine, überfordert und wollte wieder ihr Leben zurück. Ihr fehlte die Erfüllung in der Arbeit mit ihren Kunden.',
      session:
        'Nach bereits 3 Monaten der Zusammenarbeit hat Anna ihr Business auf neue Beine gestellt. Aus einem harten Business „Forged of Steel“ wurde „Anna Gräfen Health Coach“ mit neuem Webauftritt, einem 30-Tage-Onlinekurs und einem ganzheitlichen Mentorship-Angebot. Mit mehr Zeit für ihre eigene Entwicklung hat sie gleichzeitig ihren Umsatz in 4 Monaten vervierfacht.',
      result:
        '„Vielen Dank für das heutige Rollenspiel in unserem Mentorship! Das hat mir wirklich geholfen, in meiner Arbeit noch viel sicherer zu werden und genau das konnte ich auch direkt in meine nächste Session umsetzen. Ich mache enorm große Sprünge gerade durch unsere Zusammenarbeit. Einfach Danke 🙏“',
    },
    {
      name: 'Sabine Hinkel',
      gender: 'female',
      image: `${ASSET}/sabine.jpg`,
      title: 'Gründerin Dreamsketcher · Kinder- und Jugendcoach',
      teaser:
        'Vertraute Benjamin 100 % — aber nicht sich selbst. Ein Jahr später: eigenes Unternehmen, überwundene Intoleranz, verlängertes Mentorship.',
      tag: 'Mentorship · Gründung · Gesundheit',
      situation:
        '„Als ich vor einem Jahr begann mit Benjamin zu arbeiten, vertraute ich ihm 100 % – aber nicht mir. Meine berufliche und soziale Erfüllung fehlte mir und ich wollte mehr vom Leben.“ Sabine litt zudem seit 7 Jahren unter einer stark einschränkenden Fruktoseintoleranz und fühlte sich oft müde und überfordert.',
      session:
        'Als Lehrerin und Unternehmerin arbeitet Sabine heute aus dem Herzen als Jugend- und Kindercoach. Im Mentorship durfte ich sie darin unterstützen, ihr eigenes Unternehmen „Dreamsketcher“ zu gründen — Präsenz-Workshops und Online-Kurse für Jugendliche und Eltern. Superzufrieden mit den Ergebnissen hat sie ihr Mentorship bereits 3 Mal verlängert.',
      result:
        '„Ein Jahr Mentoring mit Benjamin hat mein Leben grundlegend verändert. Durch seine sensible und unterstützende Art konnte ich mich öffnen und Herausforderungen meistern. Benjamin hat mir geholfen, meine jahrelange Fruktoseintoleranz zu überwinden und ein freies Leben zu führen. Benjamin ist ein außergewöhnlicher Mentor, dem ich voll vertrauen kann.“',
    },
    {
      name: 'Bianca Gruber',
      gender: 'female',
      // Archive Kajabi asset is 100×89 placeholder; use monogram until real photo is restored
      image: '',
      title: 'NLP Master Practitioner',
      teaser:
        'Mutterschaft, Schutz und Nähe — und die Kraft, dem eigenen Kind und sich selbst wieder einen Platz im Herzen zu geben.',
      tag: 'Mentorship · Familie · Nähe',
      situation:
        '„Ich dachte, ich könnte ihn nicht so lieben, wie es richtig für eine Mutter wäre. Und ich wollte nicht, dass er mich so liebt, wie ich es mir ganz tief im Inneren gewünscht hätte. Einfach, weil ich nicht verletzen oder verletzt werden wollte.“',
      session:
        'Im Mentorship fand Bianca einen Weg, Nähe zuzulassen — zu sich, zu ihrem Kind, und zu dem, was sie sich tief im Inneren wünscht.',
      result:
        '„Benjamin ist der beste Coach, Trainer und Mentor, den ich mir vorstellen kann. Seine einzigartige Arbeitsweise und sein tiefes Verständnis für Menschen machen ihn zu einem außergewöhnlichen Begleiter auf meinem Weg der persönlichen Entwicklung. Seitdem ich dank unserer Zusammenarbeit meinem Sternchen einen Platz in meinem Herzen gegeben habe, hat auch mein Sohn einen ganz besonderen Platz in meinem Herzen bekommen. Und das macht sich bemerkbar in seinem & meinem Verhalten. Ich hätte vorher nicht geglaubt, dass sich in so kurzer Zeit so vieles (im Innen & Außen) verändern kann und kann es kaum erwarten zu entdecken, was das Leben sonst noch zu bieten hat. Ich hab es selbst in der Hand.“',
    },
  ],
  sectionLabels: {
    situation: 'Ausgangspunkt',
    session: 'Im Mentorship',
    result: 'Stimme der Kund:in',
  },
}

export const groupedList = {
  id: 'list',
  label: 'SO KOMMST DU ZU DEINEN ERGEBNISSEN',
  headline: 'In großen Schritten zu innerer Freiheit und einem selbstbestimmten Leben.',
  subline: 'Alles, was du dafür brauchst, steckt bereits in dir!',
  items: [
    'Lerne der Stimme deiner Intuition zu hören & ernte die Früchte, die folgen, wenn du konsequent DEINEM Weg folgst.',
    'Kultiviere Lebensfreude in allem, was du tust.',
    'Lerne anders mit Stress und Frustration umzugehen (& schneller Lösungen zu finden).',
    'Bekomme Klarheit über deine konkreten nächsten Umsetzungsschritte.',
    'Verändere dein Leben nach deinen Vorstellungen (auch, wenn du heute noch nicht genau weißt, wie).',
    'Lerne, wie du bewusst Ergebnisse in deinem Leben kreierst / manifestierst.',
    'Tausche dich regelmäßig mit Benjamin persönlich aus.',
    'Bekomme alle Schritte an die Hand, um dein Visionboard Realität werden zu lassen — schneller, als wenn du dich allein auf den Weg machst.',
    'Lerne dein Herz, deinen Verstand und deine Seele in allem was du tust zu integrieren.',
    'Fokussiere dich darauf, was werden soll (& nicht das, was du nicht mehr willst).',
    'Gewinne Selbstvertrauen & Klarheit in Entscheidungsprozessen.',
    'Erreiche neue Bewusstseinsstufen.',
    'Lerne Energiearbeit gezielt einzusetzen.',
    'Komm in die Umsetzung & erreiche beruflich wie privat ganz neue Ebenen.',
    'Lerne dein wertvolles Leben in Leichtigkeit und Freiheit zu leben.',
  ],
  closingQuote:
    '„Etwas Großartiges ist dir auf der Spur. Renn nicht weg. Nicht dieses Mal. Bleib stehen!“',
  cta: 'Vereinbare jetzt dein Erstgespräch',
}
export const processResults = groupedList

/**
 * ForWhomSection — TopicCard craft, 3 columns × 2 rows (6 cards).
 * Same regression TopicCard UI as forWhom; denser 3-col grid.
 */
export const outcomesList = {
  id: 'ergebnisse-liste',
  columns: 3,
  label: 'SO KOMMST DU ZU DEINEN ERGEBNISSEN',
  headline: 'In großen Schritten zu innerer Freiheit und einem selbstbestimmten Leben.',
  subline: 'Alles, was du dafür brauchst, steckt bereits in dir!',
  topics: [
    {
      title: 'Deiner Intuition folgen',
      body: 'Lerne der Stimme deiner Intuition zu hören & ernte die Früchte, die folgen, wenn du konsequent DEINEM Weg folgst.',
    },
    {
      title: 'Stress & Frustration',
      body: 'Lerne anders mit Stress und Frustration umzugehen — und schneller Lösungen zu finden.',
    },
    {
      title: 'Klare nächste Schritte',
      body: 'Bekomme Klarheit über deine konkreten nächsten Umsetzungsschritte.',
    },
    {
      title: 'Ergebnisse manifestieren',
      body: 'Lerne, wie du bewusst Ergebnisse in deinem Leben kreierst und manifestierst.',
    },
    {
      title: 'Visionboard → Realität',
      body: 'Bekomme alle Schritte an die Hand, um dein Visionboard Realität werden zu lassen — schneller, als allein.',
    },
    {
      title: 'Leichtigkeit & Freiheit',
      body: 'Lerne dein wertvolles Leben in Leichtigkeit und Freiheit zu leben.',
    },
  ],
  closingQuote: groupedList.closingQuote,
  cta: groupedList.cta,
}

/** FinalCTASection */
export const finalCTA = {
  id: 'final-cta',
  label: 'BEREIT?',
  headline: '30 Minuten Du & Ich.',
  subline:
    'Neue Perspektiven, neue Herangehensweisen, konkrete Lösungsansätze für dein Leben in Fülle. Kostenlos. Unverbindlich.',
  cta: 'Termin sichern',
  backgroundUrl: `${ASSET}/benjamin-portrait-25.jpg`,
  portraitUrl: `${ASSET}/benjamin.jpg`,
  imageUrl: `${ASSET}/benjamin.jpg`,
  imageAlt: 'Benjamin Kurtz',
}

export const simpleCta = {
  label: 'LASS UNS IN KONTAKT KOMMEN',
  headline: '30 Minuten Du & Ich.',
  body: 'Neue Perspektiven, neue Herangehensweisen, konkrete Lösungsansätze für dein Leben in Fülle.',
  cta: 'Erstgespräch vereinbaren',
}

export const ctaImage = finalCTA

export const booking = {
  label: 'ERSTGESPRÄCH',
  headline: 'Vereinbare jetzt dein Erstgespräch.',
  subline:
    'Kostenlos und unverbindlich. 30 Minuten. Ich schaue mit dir gemeinsam, was dich bewegt — und ob das Integrale Mentorship der nächste sinnvolle Schritt ist.',
  formPlaceholder: '',
  directBookingText: 'Du willst direkt einen Termin wählen?',
  directBookingCta: 'Erstgespräch buchen',
  directBookingUrl: 'https://tidycal.com/bkacademy/mentorship',
  calendarUrl:
    'https://tidycal.com/bkacademy/mentorship?embedded=1',
  formTopics: [
    'Business & Berufung',
    'Beziehungen',
    'Klarheit / Purpose',
    'Manifestation & Umsetzung',
    'Persönliche Entwicklung',
    'Sonstiges',
  ],
}

export const faq = {
  bigLabel: 'FRAGEN & ANTWORTEN',
  headline: 'Häufige Fragen zum Integralen Mentorship',
  items: [
    {
      question: 'Über welchen Zeitraum hinweg begleitest du mich?',
      answer:
        'WENN das Mentoring gerade genau das Richtige ist, legen wir das gemeinsam fest. Erfahrungsgemäß arbeite ich mit meinen Mentees zwischen 6 und 12 Monaten zusammen — einfach, weil dir das die Chance gibt, dranzubleiben und Herausforderungen auf dem Weg schnell aus dem Weg zu räumen. Du wirst allerdings sehr schnell erste Ergebnisse sehen. Probier doch einfach mal aus, wie es sich anfühlen würde mit mir zu arbeiten, und vereinbare einen Termin für dein Erstgespräch.',
    },
    {
      question: 'Was unterscheidet das integrale Mentorship von deinen Coachings?',
      answer:
        'Bei einem Coaching begleite ich dich nicht über einen längeren Zeitraum hinweg, sondern ich widme mich mit dir punktuell einem ganz bestimmten Thema. Auch wenn Veränderungen im Innen innerhalb von Sekunden passieren können, dauert die Umsetzung im Außen erfahrungsgemäß länger. Und erfordert darüber hinaus Fokus, Mut und Selbstverantwortung. Wenn du dir also einen erfahrenen Wegbegleiter wünschst, ist das Mentorship besser geeignet.',
    },
    {
      question: 'Kannst du mir Resultate garantieren?',
      answer:
        'Yes! Resultate sind das, was resultiert. Und dein Commitment für die Auseinandersetzung mit dir und deinem außergewöhnlichen Leben wird außergewöhnliche Ergebnisse mit sich bringen — WENN du ins Tun kommst. Der magische Dreiklang: Selbsterkenntnis · Entscheidungen treffen · Umsetzen.',
    },
    {
      question: 'Bietest du auch Präsenz-Treffen an?',
      answer:
        'Die kurze Antwort lautet: Ja. Es gibt einige, die es vorziehen, dass wir uns persönlich treffen. Je nachdem, wo dein Standort ist, bekommen wir das mit meinen Reiseplänen gut geregelt, vor allem in einer Hybrid-Variante, einem Wechsel zwischen Präsenz und Online-Treffen. Sprich mich gern darauf an in deinem ersten kostenlosen Call.',
    },
    {
      question: 'Ich habe eine weitere Frage, aber keine Zeit für ein Video-Call!',
      answer:
        'Kein Problem. Dafür habe ich eine gute Lösung. Sende mir eine Nachricht über mein VideoAsk-Tool. Dort kannst du mit Benjamin per Video-, Sprach- oder Textnachricht in Kontakt treten: https://ask.benjaminkurtz.de/fl4a1ara8',
    },
  ],
}

export const footer = {
  brand: 'Benjamin Kurtz Academy LLC',
  disclaimerLinkKeyword: 'Erstgespräch',
  tagline: 'Integrales Mentorship\nThe Light Call',
  contactLabel: 'Kontaktiere mich:',
  legalLabel: 'Rechtliches',
  followLabel: 'Folgen',
  reachOutLabel: 'REACH OUT',
  disclaimerTitle: 'Rechtlicher Hinweis',
  disclaimer:
    'Mentorship und Coaching ersetzen keine medizinische oder psychiatrische Behandlung. Bei Unklarheiten wähle gern ein Erstgespräch, in dem ich dich dazu kompetent beraten kann.',
  introCallLabel: 'Erstgespräch',
  copyright: {
    prefix: 'Alle Rechte vorbehalten. Home:',
    homeLabel: 'thelightcall.com',
    homeUrl: 'https://thelightcall.com',
  },
  contact: [
    { label: 'WhatsApp', url: 'https://wa.me/4915121231244', icon: 'whatsapp' },
    { label: 'E-Mail', url: 'mailto:hello@benjaminkurtz.de', icon: 'mail' },
    { label: 'Instagram', url: 'https://instagram.com/benjaminkurtz_de', icon: 'instagram' },
  ],
  legalLinks: [
    { label: 'Terms', url: 'https://www.benjaminkurtz.de/terms/' },
    { label: 'Privacy', url: 'https://www.benjaminkurtz.de/privacy/' },
    { label: 'Imprint', url: 'https://www.benjaminkurtz.de/imprint/' },
  ],
  social: [
    { label: 'Spotify', url: 'https://open.spotify.com/' },
    { label: 'Apple', url: 'https://podcasts.apple.com/' },
    { label: 'YouTube', url: 'https://youtube.com/' },
  ],
}

export const services_extra = {}
export const researchers = {
  authorBigLabel: '',
  authorHeadline: '',
  authorLabel: '',
  accordion: { readMore: 'Mehr', readLess: 'Weniger' },
  authors: [],
}
export const references = { label: '', headline: '', items: [] }
export const testimonials = { label: '', headline: '', items: [] }
export const podcasts = []
export const podcastVideo = {}
export const statement_extra = {}
export const ui = {
  readMore: 'Mehr lesen',
  readLess: 'Weniger lesen',
  loading: 'Lädt…',
  close: 'Schließen',
}

export const transcriptPage = {}
export const notFound = {
  title: 'Seite nicht gefunden',
  body: 'Diese Seite gibt es hier nicht.',
  cta: 'Zur Startseite',
}

export const menu = {
  overviewLabel: 'Überblick',
  header: {
    logo: {
      src: 'https://media.thelightcall.com/logos/BKA%20logo%20500%20px%20black.png',
      alt: 'Benjamin Kurtz Academy',
    },
    cta: {
      label: 'Erstgespräch',
      anchor: '#booking',
    },
    variant: 'sidecar',
    mainNav: [
      { label: 'Mentorship', anchor: null, hasSubmenu: true },
      { label: 'Ergebnisse', anchor: null, hasSubmenu: true },
      { label: 'Gespräch', anchor: '#booking', hasSubmenu: false },
    ],
  },
  items: [
    {
      id: 'mentorship',
      label: 'Mentorship',
      order: 10,
      children: [
        { id: 'm-hero', label: 'Start', anchor: '#hero', order: 11 },
        { id: 'm-what', label: 'Das Problem', anchor: '#was-ist', order: 12 },
        { id: 'm-frei', label: 'Freiheit', anchor: '#freiheit', order: 13 },
        { id: 'm-domino', label: 'Domino', anchor: '#domino', order: 14 },
        { id: 'm-fuelle', label: 'Leben in Fülle', anchor: '#leben-in-fuelle', order: 15 },
        { id: 'm-cases', label: 'Kund:innen', anchor: '#cases', order: 16 },
        { id: 'm-for', label: 'Für wen?', anchor: '#fuer-wen', order: 17 },
      ],
    },
    {
      id: 'results',
      label: 'Ergebnisse',
      order: 20,
      children: [
        { id: 'm-cases', label: 'Kund:innen', anchor: '#cases', order: 21 },
        { id: 'm-faq', label: 'FAQ', anchor: '#faq', order: 22 },
      ],
    },
    {
      id: 'sites',
      label: 'The Light Call',
      order: 30,
      children: [
        { id: 's-reg', label: 'Regression', href: '/regression/de', order: 31 },
        { id: 's-men', label: 'Mentorship', href: '/mentorship/de', order: 32 },
        { id: 's-rei', label: 'Reiki', href: '/reiki/de', order: 33 },
      ],
    },
  ],
}

export default {
  meta,
  header,
  hero,
  heroImage,
  fullBleedHero,
  services,
  welcome,
  statement,
  storyQuote,
  quoteSection,
  whatIs,
  counterbalance,
  puzzleBand,
  freedomStatement,
  brickBand,
  kungFuBand,
  outcomesIntro,
  mediaText,
  freedomPuzzle,
  storyPartA,
  storyPartB,
  storySequence,
  dominoStory,
  quoteBand,
  secretsQuote,
  pillars,
  processSection,
  mentorshipBenefits,
  forWhom,
  outcomesList,
  about,
  groupedList,
  processResults,
  cases,
  simpleCta,
  finalCTA,
  ctaImage,
  booking,
  faq,
  footer,
  ui,
  researchers,
  references,
  testimonials,
  podcasts,
  podcastVideo,
  transcriptPage,
  notFound,
  menu,
}
